import { JSX, useContext, useEffect, useState } from "react";
import { getPciDashboardScenarioChangeDisplay, getRoundingDisplay, getScenarioDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { ColumnConfig, printNumberAsString, TextAlignment, wafflesKeyColumnName, WafflesThemes } from "@app/shared/waffles/exports";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ClaimsActivityProviderScenario } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityProviderScenario";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityVolumeLabelEnum";
import { PciDashboardScenarioTypeEnum } from "@app/projects/network-assessment/pci-dashboard/shared";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";
import { formatDateTime } from "@app/shared/formatDateTime";
import { QESButton } from "@app/shared/qes-standards/Button";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import { Waffles } from "@app/shared/waffles/Waffles";

interface ClaimsActivityProviderScenarioTileProps {
  data: ClaimsActivityProviderScenario[];
  loading: boolean;
  onDashboardDetailsViewClick: (
    claimsLabel: ClaimsActivityVolumeLabelEnum,
    providerNetworkType: ProviderNetworkTypeEnum,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => void;
}
interface ClaimsActivityProviderScenarioWaffleToppings {
  claimsVolumeLabel: string;
  changeCount: JSX.Element;
  changePercentage: JSX.Element;
  comparePercentage: string;
  compareServicingCount: string | JSX.Element;
  percentage: string;
  servicingCount: string | JSX.Element;
  [wafflesKeyColumnName]: string;
}

const ClaimsActivityProviderScenarioTile = (props: ClaimsActivityProviderScenarioTileProps): JSX.Element => {
  const [tableData, setTableData] = useState<ClaimsActivityProviderScenarioWaffleToppings[]>([]);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);

  const columnConfig: ColumnConfig = {
    changeCount: {
      alignment: TextAlignment.Center,
      displayText: "Change",
      noSort: true,
      testId: "change-count",
    },
    changePercentage: {
      alignment: TextAlignment.Center,
      displayText: "Change",
      noSort: true,
      testId: "change-percentage",
    },
    claimsVolumeLabel: {
      alignment: TextAlignment.Left,
      displayText: "",
      noSort: true,
      testId: "claims-volume-label",
    },
    comparePercentage: {
      alignment: TextAlignment.Center,
      displayText: `${getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)} Percentage`,
      noSort: true,
      testId: "compare-percentage",
    },
    compareServicingCount: {
      alignment: TextAlignment.Center,
      displayText: `${getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)} Count`,
      noSort: true,
      testId: "compare-servicing-count",
    },
    percentage: {
      alignment: TextAlignment.Center,
      displayText: "Latest Timeslice Percentage",
      noSort: true,
      testId: "percentage",
    },
    servicingCount: {
      alignment: TextAlignment.Center,
      displayText: "Latest Timeslice Count",
      noSort: true,
      testId: "servicing-count",
    },
  };

  useEffect(() => {
    if (props.data) {
      /* oxlint-disable sort-keys */
      const newTableData: ClaimsActivityProviderScenarioWaffleToppings[] = props.data.map((s, index) => {
        return {
          [wafflesKeyColumnName]: s.claimsVolumeLabel || index.toString(),
          claimsVolumeLabel: s.claimsVolumeLabel || "---",
          servicingCount: getCountDisplay(s, "servicingCount"),
          compareServicingCount: getCountDisplay(s, "compareServicingCount"),
          changeCount: getPciDashboardScenarioChangeDisplay(s.changeCount),
          percentage: getRoundingDisplay(s.percentage, true),
          comparePercentage: getRoundingDisplay(s.comparePercentage, true),
          changePercentage: getPciDashboardScenarioChangeDisplay(s.changePercentage, true, true),
        }
      });
      /* oxlint-enable sort-keys */
      setTableData(newTableData);
    }
  }, [props.data, pciDashboardFiltersContext.displayedFilters]);

  const getCountDisplay = (providerScenario: ClaimsActivityProviderScenario, key: keyof ClaimsActivityProviderScenario) => {
    if (!providerScenario[key]) {
      return "---";
    }

    return (
      <QESButton
        onClick={() => props.onDashboardDetailsViewClick(
          providerScenario.claimsVolumeLabel,
          ProviderNetworkTypeEnum.Network,
          key === "compareServicingCount" ? pciDashboardFiltersContext.displayedFilters.compareScenario : PciDashboardScenarioTypeEnum.Original
        )}
        qesTestId={`claims-activity__provider-scenario--dashboard-details-${providerScenario.claimsVolumeLabel}`}
        qesType="secondary"
      >
        {printNumberAsString(providerScenario[key] as number, "0")}
      </QESButton>
    );
  }

  const rowDecorator = (row: ClaimsActivityProviderScenarioWaffleToppings) => {
    if (row.claimsVolumeLabel === ClaimsActivityVolumeLabelEnum.Total) {
      return "footer";
    }
  }

  return (
    <DashboardTile
      classNames="pci-dashboard__summary-tile"
      loading={props.loading}
      subtitle={`Comparison of ${formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated)} and ${getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)}`}
      title="Network"
      widthClass="col-md-12"
    >
      <Waffles<ClaimsActivityProviderScenarioWaffleToppings>
        columns={columnConfig}
        data={tableData}
        disableStickyHeader={true}
        fullWidth={true}
        keyColumns={["claimsVolumeLabel"]}
        rowDecorator={rowDecorator}
        testId="claims-activity__provider-scenario--network"
        theme={WafflesThemes.Simple}
      />
    </DashboardTile>
  )
}

export default ClaimsActivityProviderScenarioTile;